import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { cadStr } from "@/lib/pricing";
import { fmtDate } from "@/components/merchant/billing/KeepPriceNotice";

function Row({ label, value, testid, strong }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className={strong ? "font-display text-lg text-slate-900" : "font-semibold text-slate-900"} data-testid={testid}>{value}</span>
    </div>
  );
}

// Server quote for a plan change (Stripe's own proration preview for upgrades). Nothing is charged here.
export function PlanChangePreview({ plan, cadence, onReady }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    setP(null);
    setErr(null);
    onReady(false);
    api.post("/billing/sg/change-plan/preview", { plan, cadence })
      .then((r) => { if (live) { setP(r.data); onReady(true); } })
      .catch((e) => {
        const d = e?.response?.data?.detail;
        if (live) setErr(typeof d === "string" ? d : "We couldn't get the price right now. Try again in a moment.");
      });
    return () => { live = false; };
  }, [plan, cadence, onReady]);

  if (err) return <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800" data-testid="plan-preview-error">{err}</div>;
  if (!p) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-500 inline-flex items-center gap-2 w-full" data-testid="plan-preview-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Getting your exact price…
      </div>
    );
  }
  const up = p.kind === "upgrade";
  const kept = p.ends_kept_price;
  return (
    <div className="space-y-3" data-testid="plan-preview">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm space-y-2">
        {up ? (
          <Row label="Due today (prorated)" strong testid="plan-preview-due-now"
               value={`${cadStr(p.due_now_cad)}${p.tax_cad ? ` incl. ${cadStr(p.tax_cad)} tax` : ""}`} />
        ) : (
          <Row label="Starts" testid="plan-preview-effective" value={fmtDate(p.effective_at) || "End of your paid period"} />
        )}
        <Row label={up ? "Then" : "Price"} testid="plan-preview-price" value={`${cadStr(p.price_cad)}/${p.period}`} />
        <Row label="Fee on new verified sales" testid="plan-preview-fee" value={`${p.fee_pct}% or ${cadStr(p.fee_minimum_cad)}`} />
        {p.credit_cad && (
          <div className="text-xs text-emerald-800" data-testid="plan-preview-credit">
            {cadStr(p.credit_cad)} of unused time goes to your account credit for future invoices.
          </div>
        )}
        {p.test_bypass && <div className="text-xs text-amber-700" data-testid="plan-preview-test-note">Test account: no card is charged.</div>}
      </div>
      {kept && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2" data-testid="plan-preview-kept-price-ends">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            This ends your kept {kept.plan_label} price of <strong>{cadStr(kept.current_price_cad)}/{kept.period}</strong>.
            {kept.new_customers_price_cad !== kept.current_price_cad && <> If you switch back later, it's {cadStr(kept.new_customers_price_cad)}/{kept.period}.</>}
          </span>
        </div>
      )}
    </div>
  );
}
